import moment from 'moment';
import { batchActions } from './utils';
import { setData } from './data';
import { setStatus, unsetStatus } from './status';
import {
  LOADING,
  ERROR,
  UNAUTHORIZED,
  selectNetworkMetadataStatus,
  selectNetworkTopPodcastsStatus,
  selectNetworkTopEpisodesStatus,
  selectDatesAvailable,
} from './selectors';
import { toJSON, PODCASTERS_SERVICE } from '../lib/Request';
import { Thunk, NetworkMetadata, TopPodcast, Paginated, SparkLine, TopEpisode } from '../types';

export const loadNetworkMetadata = (networkId: string): Thunk => async (
  dispatch,
  getState,
  { request },
) => {
  const key = 'networkMetadata';
  const endpoint = `${PODCASTERS_SERVICE}/v0/networks/${networkId}/metadata`;

  /* istanbul ignore next */
  if (selectNetworkMetadataStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'audience/LOAD_NETWORK_METADATA_REQUEST'));

  try {
    const data: NetworkMetadata = await request.get(endpoint).then(toJSON);

    dispatch(
      batchActions(
        [setData({ [key]: data }), unsetStatus([key])],
        'audience/LOAD_NETWORK_METADATA_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        if (err.message === '403') {
          dispatch(setStatus({ [key]: UNAUTHORIZED }, 'audience/LOAD_UNAUTHORIZED'));
        } else {
          dispatch(setStatus({ [key]: ERROR }, 'audience/LOAD_NETWORK_METADATA_ERROR'));
        }
        break;
      }

      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};

export const loadTopPodcasts = (networkId: string): Thunk => async (
  dispatch,
  getState,
  { request },
) => {
  const key = 'networkTopPodcasts';
  const end = selectDatesAvailable(getState()).until;
  const start = moment(end)
    .subtract(6, 'days')
    .format('YYYY-MM-DD');
  const endpoint = `${PODCASTERS_SERVICE}/v0/networks/${networkId}/topShows?start=${start}&end=${end}`;

  /* istanbul ignore next */
  if (selectNetworkTopPodcastsStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'audience/LOAD_NETWORK_TOP_PODCASTS_REQUEST'));

  try {
    const data: {
      shows: TopPodcast[];
    } & Paginated = await request.get(endpoint).then(toJSON);

    dispatch(
      batchActions(
        [setData({ [key]: data.shows }), unsetStatus([key])],
        'audience/LOAD_NETWORK_TOP_PODCASTS_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        dispatch(setStatus({ [key]: ERROR }, 'audience/LOAD_NETWORK_TOP_PODCASTS_ERROR'));
        break;
      }

      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};

export const loadTopEpisodes = (networkId: string): Thunk => async (
  dispatch,
  getState,
  { request },
) => {
  const key = 'networkTopEpisodes';
  const end = selectDatesAvailable(getState()).until;
  const start = moment(end)
    .subtract(6, 'days')
    .format('YYYY-MM-DD');
  const endpoint = `${PODCASTERS_SERVICE}/v0/networks/${networkId}/topEpisodes?start=${start}&end=${end}`;

  /* istanbul ignore next */
  if (selectNetworkTopEpisodesStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'audience/LOAD_NETWORK_TOP_EPISODES_REQUEST'));

  try {
    const data: {
      episodes: TopEpisode[];
    } & Paginated = await request.get(endpoint).then(toJSON);

    dispatch(
      batchActions(
        [setData({ [key]: data.episodes }), unsetStatus([key])],
        'audience/LOAD_NETWORK_TOP_EPISODES_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        dispatch(setStatus({ [key]: ERROR }, 'audience/LOAD_NETWORK_TOP_EPISODES_ERROR'));
        break;
      }

      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};
